// src/LandingPage.js
import React, { useState } from "react";
import "./index.css";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faTimes,
  faShieldHalved,
  faBolt,
  faChartLine,
  faArrowRight,
  faUsers,
  faMobileScreen,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import MyPaycheckCalculatorPreview from "./resources/MyPaycheckCalculatorScreenshots.png";

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

/* ──────────────────────────────────────────── */

const LandingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#09090b] text-white antialiased">
      {/* ── NAV ─────────────────────────────── */}
      <header className="fixed w-full bg-[#09090b]/90 backdrop-blur-xl z-50 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px]">
          <a href="#hero" className="text-xl font-bold tracking-tight flex items-center gap-2">
            <span className="bg-gradient-to-br from-emerald-400 to-emerald-600 text-transparent bg-clip-text">Bird</span>
            <span>Software</span>
          </a>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-400">
            <a href="#portfolio" className="hover:text-white transition">Portfolio</a>
            <a href="#about" className="hover:text-white transition">About</a>
            <a href="#values" className="hover:text-white transition">Values</a>
            <a href="#contact" className="hover:text-white transition">Contact</a>
          </nav>

          <button
            className="md:hidden text-zinc-400 hover:text-white"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} className="text-xl" />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/[0.06] bg-[#09090b] px-6 pb-6 pt-4 space-y-4 text-sm font-medium">
            {["Portfolio", "About", "Values", "Contact"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="block text-zinc-300 hover:text-white"
                onClick={() => setMenuOpen(false)}
              >
                {item}
              </a>
            ))}
          </div>
        )}
      </header>

      <main>
        {/* ── HERO ────────────────────────────── */}
        <motion.section
          id="hero"
          className="relative pt-40 pb-28 px-6 overflow-hidden"
          variants={fade}
          initial="hidden"
          animate="visible"
        >
          {/* Background glow */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px]" />
          </div>

          <div className="relative max-w-5xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/5 text-emerald-400 text-sm font-medium mb-8"
              variants={fade}
            >
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              Building the future of personal finance
            </motion.div>

            <motion.h1
              className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.08] mb-8"
              variants={fade}
            >
              Software that
              <br />
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-400 text-transparent bg-clip-text">
                empowers millions
              </span>
            </motion.h1>

            <motion.p
              className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-12 leading-relaxed"
              variants={fade}
            >
              Bird Software builds best-in-class mobile applications that help people
              understand their money, make smarter decisions, and take control of their financial lives.
            </motion.p>

            <motion.div className="flex flex-wrap justify-center gap-4" variants={fade}>
              <a
                href="#portfolio"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-8 py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/25"
              >
                Explore Our Apps
                <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
              </a>
              <a
                href="#about"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white font-semibold px-8 py-4 rounded-xl transition-all"
              >
                Learn More
              </a>
            </motion.div>
          </div>
        </motion.section>

        {/* ── METRICS BAR ─────────────────────── */}
        <section className="border-y border-white/[0.06] bg-white/[0.02]">
          <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "50", label: "States Covered" },
              { value: "4.7★", label: "App Store Rating" },
              { value: "6", label: "Tax Years Supported" },
              { value: "0", label: "Data Collected" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-zinc-500 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── PORTFOLIO / FLAGSHIP ────────────── */}
        <motion.section
          id="portfolio"
          className="py-28 px-6"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <div className="max-w-7xl mx-auto">
            <motion.div className="text-center mb-16" variants={fade}>
              <p className="text-emerald-400 font-semibold text-sm tracking-wide uppercase mb-3">Portfolio</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight">Our Flagship Product</h2>
            </motion.div>

            <motion.div
              className="relative bg-gradient-to-br from-zinc-900 to-zinc-950 border border-white/[0.06] rounded-3xl overflow-hidden"
              variants={fade}
            >
              <div className="flex flex-col lg:flex-row">
                {/* Copy */}
                <div className="lg:w-1/2 p-10 lg:p-16 flex flex-col justify-center">
                  <span className="inline-block text-xs font-bold tracking-widest text-emerald-400 uppercase mb-4">
                    Finance · iOS &amp; Web
                  </span>
                  <h3 className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight">
                    My Paycheck Calculator
                  </h3>
                  <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                    The most comprehensive take-home pay calculator on the App Store.
                    Federal taxes, all 50 state taxes, FICA, retirement contributions,
                    stock compensation — calculated to the penny for tax years 2021–2026.
                  </p>

                  <ul className="space-y-4 mb-10">
                    {[
                      "Federal, state & local tax for all 50 states",
                      "Stock compensation (ISOs, NSOs, RSUs)",
                      "Side-by-side scenario comparison",
                      "100% private — your data never leaves your device",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3 text-zinc-300">
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://apps.apple.com/us/app/my-paycheck-calculator/id6449910460"
                      className="inline-flex items-center gap-2 bg-white text-black font-semibold px-6 py-3 rounded-xl hover:bg-zinc-200 transition"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon icon={faApple} className="text-lg" />
                      App Store
                    </a>
                    <a
                      href="https://mypaycheckcalculator.com"
                      className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 font-semibold px-6 py-3 rounded-xl transition"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon icon={faGlobe} className="text-sm" />
                      Website
                    </a>
                  </div>
                </div>

                {/* Screenshot */}
                <div className="lg:w-1/2 relative flex items-center justify-center p-10 lg:p-16">
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5" />
                  <img
                    className="relative w-72 max-w-full rounded-[2rem] shadow-2xl shadow-black/40 ring-1 ring-white/10"
                    src={MyPaycheckCalculatorPreview}
                    alt="My Paycheck Calculator app screenshot"
                    loading="lazy"
                  />
                </div>
              </div>
            </motion.div>

            {/* More apps teaser */}
            <motion.div
              className="mt-8 bg-zinc-900/60 border border-white/[0.06] rounded-2xl p-8 text-center"
              variants={fade}
            >
              <p className="text-zinc-400 text-lg">
                More apps in development. <span className="text-emerald-400 font-medium">Stay tuned.</span>
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* ── ABOUT ───────────────────────────── */}
        <motion.section
          id="about"
          className="py-28 px-6 border-t border-white/[0.06]"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-16" variants={fade}>
              <p className="text-emerald-400 font-semibold text-sm tracking-wide uppercase mb-3">About</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
                Built to scale.
                <br />
                <span className="text-zinc-500">Designed to last.</span>
              </h2>
              <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
                Bird Software is an app development studio focused on building a portfolio of
                best-in-class mobile products in personal finance. We combine deep domain
                expertise with modern engineering to create tools that millions of people rely on.
              </p>
            </motion.div>

            <motion.div className="grid md:grid-cols-3 gap-6" variants={stagger}>
              {[
                {
                  icon: faMobileScreen,
                  title: "Mobile-First",
                  desc: "Native iOS and Android apps built for performance, accessibility, and delight.",
                },
                {
                  icon: faUsers,
                  title: "User-Obsessed",
                  desc: "Every feature starts with a real problem. We ship solutions, not features.",
                },
                {
                  icon: faChartLine,
                  title: "Data-Driven",
                  desc: "Tax brackets, withholding rules, and deduction logic — accurate to the cent.",
                },
              ].map((item) => (
                <motion.div
                  key={item.title}
                  className="bg-zinc-900/40 border border-white/[0.06] rounded-2xl p-8 text-center hover:border-emerald-500/15 transition-colors"
                  variants={fade}
                >
                  <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <FontAwesomeIcon icon={item.icon} className="text-emerald-400 text-xl" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-zinc-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* ── VALUES ──────────────────────────── */}
        <motion.section
          id="values"
          className="py-28 px-6 bg-gradient-to-b from-transparent to-emerald-500/[0.03]"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-16" variants={fade}>
              <p className="text-emerald-400 font-semibold text-sm tracking-wide uppercase mb-3">Our Principles</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight">What we stand for</h2>
            </motion.div>

            <motion.div className="grid sm:grid-cols-2 gap-6" variants={stagger}>
              {[
                {
                  icon: faShieldHalved,
                  title: "Privacy by Default",
                  desc: "We don't collect, store, or sell user data. Period. Your financial information stays on your device.",
                },
                {
                  icon: faBolt,
                  title: "Relentless Accuracy",
                  desc: "Tax code is complex. We don't cut corners. Every bracket, every deduction, every edge case — verified and tested.",
                },
                {
                  icon: faChartLine,
                  title: "Continuous Improvement",
                  desc: "Tax laws change. We ship updates before deadlines, not after. Six tax years supported and counting.",
                },
                {
                  icon: faGlobe,
                  title: "Access for Everyone",
                  desc: "Understanding your paycheck shouldn't require a CPA. We make complex tax math simple and free to try.",
                },
              ].map((val) => (
                <motion.div
                  key={val.title}
                  className="flex gap-5 bg-zinc-900/40 border border-white/[0.06] rounded-2xl p-8 hover:border-emerald-500/15 transition-colors"
                  variants={fade}
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center shrink-0">
                    <FontAwesomeIcon icon={val.icon} className="text-emerald-400 text-lg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">{val.title}</h3>
                    <p className="text-zinc-500 leading-relaxed text-[15px]">{val.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* ── CONTACT CTA ─────────────────────── */}
        <motion.section
          id="contact"
          className="py-28 px-6 border-t border-white/[0.06]"
          variants={fade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Let's build something
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-400 text-transparent bg-clip-text">
                together.
              </span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10 leading-relaxed">
              Have a question, partnership idea, or just want to say hello?
              We'd love to hear from you.
            </p>
            <a
              href="mailto:info@birdsoftwarellc.com"
              className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-8 py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-500/25 text-lg"
            >
              <FontAwesomeIcon icon={faEnvelope} />
              info@birdsoftwarellc.com
            </a>
          </div>
        </motion.section>
      </main>

      {/* ── FOOTER ────────────────────────────── */}
      <footer className="border-t border-white/[0.06] bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="text-xl font-bold mb-3 flex items-center gap-2">
                <span className="bg-gradient-to-br from-emerald-400 to-emerald-600 text-transparent bg-clip-text">Bird</span>
                <span>Software</span>
              </div>
              <p className="text-zinc-500 text-sm leading-relaxed max-w-sm">
                An independent app studio building best-in-class personal finance tools
                for iOS and the web.
              </p>
            </div>

            {/* Apps */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider mb-4">Products</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="https://apps.apple.com/us/app/my-paycheck-calculator/id6449910460" className="text-zinc-500 hover:text-white transition" target="_blank" rel="noopener noreferrer">
                    My Paycheck Calculator
                  </a>
                </li>
                <li>
                  <a href="https://mypaycheckcalculator.com" className="text-zinc-500 hover:text-white transition" target="_blank" rel="noopener noreferrer">
                    Web Calculator
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="https://mypaycheckcalculator.com/privacy-policy.html" className="text-zinc-500 hover:text-white transition" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="https://mypaycheckcalculator.com/terms-of-service.html" className="text-zinc-500 hover:text-white transition" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/[0.06] mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-zinc-600 text-sm">&copy; {new Date().getFullYear()} Bird Software LLC. All rights reserved.</p>
            <p className="text-zinc-700 text-xs">Miami, Florida</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
